import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Badge,
  UncontrolledTooltip,
  Spinner
} from 'reactstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { BASE_URL } from '../constants/apiConfig';
import './ScoringCriteria.css';

const ScoringCriteria = () => {
  const [criteria, setCriteria] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCriteria, setEditingCriteria] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [totalWeight, setTotalWeight] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    label: '',
    description: '',
    weight: 0,
    minRange: 0,
    maxRange: 10,
    rubricText: ''
  });

  useEffect(() => {
    loadCriteria();
  }, []);

  const calculateTotalWeight = () => {
    const total = criteria.reduce((sum, item) => sum + Number(item.weight), 0);
    setTotalWeight(total);
  };

  useEffect(() => {
    calculateTotalWeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria]);

  const loadCriteria = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/scoring-criteria`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        // Transform API data to match component format
        const transformedCriteria = response.data.data.map(item => ({
          id: item.id,
          label: item.name,
          description: item.description,
          weight: Number(item.weight),
          minRange: Number(item.min_score),
          maxRange: Number(item.max_score),
          rubricText: item.rubric_text,
          sortOrder: item.sort_order,
          isActive: item.is_active
        }));
        setCriteria(transformedCriteria);
      }
    } catch (error) {
      console.error('Error loading criteria:', error);
      toast.error('Failed to load scoring criteria');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      label: '',
      description: '',
      weight: 0,
      minRange: 0,
      maxRange: 10,
      rubricText: ''
    });
    setEditingCriteria(null);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (isModalOpen) {
      resetForm();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const showAlert = (message, type = 'success') => {
    setAlert({ show: true, message, type });
    setTimeout(() => {
      setAlert({ show: false, message: '', type: '' });
    }, 3000);
  };

  const validateForm = () => {
    if (!formData.label.trim()) {
      showAlert('Label is required', 'danger');
      return false;
    }
    if (!formData.description.trim()) {
      showAlert('Description is required', 'danger');
      return false;
    }
    if (formData.weight < 0 || formData.weight > 100) {
      showAlert('Weight must be between 0 and 100', 'danger');
      return false;
    }
    if (Number(formData.minRange) >= Number(formData.maxRange)) {
      showAlert('Min range must be less than max range', 'danger');
      return false;
    }
    
    // Check if adding/editing this criteria would exceed total weight of 100
    const currentTotal = criteria
      .filter(c => editingCriteria ? c.id !== editingCriteria.id : true)
      .reduce((sum, item) => sum + Number(item.weight), 0);
    
    if (currentTotal + Number(formData.weight) > 100) {
      showAlert(`Total weight cannot exceed 100. Current total: ${currentTotal}. Available: ${100 - currentTotal}`, 'danger');
      return false;
    }

    if (criteria.length >= 10 && !editingCriteria) {
      showAlert('Maximum 10 criteria allowed', 'danger');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const apiData = {
        name: formData.label,
        description: formData.description,
        weight: Number(formData.weight),
        min_score: Number(formData.minRange),
        max_score: Number(formData.maxRange),
        rubric_text: formData.rubricText,
        sort_order: criteria.length + 1,
        is_active: true
      };

      if (editingCriteria) {
        // Update existing criteria
        const response = await axios.put(
          `${BASE_URL}/api/scoring-criteria/${editingCriteria.id}`,
          apiData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        if (response.data.success) {
          toast.success('Criteria updated successfully');
          loadCriteria();
        }
      } else {
        // Add new criteria
        const response = await axios.post(
          `${BASE_URL}/api/scoring-criteria`,
          apiData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        if (response.data.success) {
          toast.success('Criteria added successfully');
          loadCriteria();
        }
      }

      toggleModal();
    } catch (error) {
      console.error('Error saving criteria:', error);
      toast.error(error.response?.data?.message || 'Failed to save criteria');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingCriteria(item);
    setFormData({
      label: item.label,
      description: item.description,
      weight: item.weight,
      minRange: item.minRange,
      maxRange: item.maxRange,
      rubricText: item.rubricText
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this criteria?')) {
      try {
        const token = sessionStorage.getItem('token');
        const response = await axios.delete(
          `${BASE_URL}/api/scoring-criteria/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        if (response.data.success) {
          toast.success('Criteria deleted successfully');
          loadCriteria();
        }
      } catch (error) {
        console.error('Error deleting criteria:', error);
        toast.error(error.response?.data?.message || 'Failed to delete criteria');
      }
    }
  };

  const handleReorder = async (index, direction) => {
    const newCriteria = [...criteria];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= criteria.length) return;
    
    [newCriteria[index], newCriteria[targetIndex]] = [newCriteria[targetIndex], newCriteria[index]];
    
    try {
      const token = sessionStorage.getItem('token');
      // Send reordered criteria to API
      const reorderedData = newCriteria.map((item, idx) => ({
        id: item.id,
        sort_order: idx + 1
      }));
      
      const response = await axios.put(
        `${BASE_URL}/api/scoring-criteria/reorder/batch`,
        { criteria: reorderedData },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.data.success) {
        setCriteria(newCriteria);
        toast.success('Criteria reordered successfully');
      }
    } catch (error) {
      console.error('Error reordering criteria:', error);
      toast.error('Failed to reorder criteria');
    }
  };

  return (
    <Container fluid className="container">
      {alert.show && (
        <Alert color={alert.type} className="alert-floating">
          {alert.message}
        </Alert>
      )}

      <Row className="mb-4">
        <Col>
          <div className="page-header">
            <div>
              <h2 className="page-title">
                <i className="bi bi-clipboard-check me-2"></i>
                Scoring Criteria Management
              </h2>
              <p className="page-subtitle">Configure evaluation criteria for fellowship applications</p>
            </div>
            <Button
              color="primary"
              onClick={toggleModal}
              disabled={criteria.length >= 10}
              className="btn-add-criteria"
            >
              <i className="bi bi-plus-circle me-2"></i>
              Add Criteria
            </Button>
          </div>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md="4">
          <Card className="summary-card">
            <CardBody>
              <div className="summary-content">
                <div className="summary-icon bg-primary">
                  <i className="bi bi-list-check"></i>
                </div>
                <div>
                  <div className="summary-label">Total Criteria</div>
                  <div className="summary-value">{criteria.length} / 10</div>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md="4">
          <Card className="summary-card">
            <CardBody>
              <div className="summary-content">
                <div className={`summary-icon ${totalWeight === 100 ? 'bg-success' : 'bg-warning'}`}>
                  <i className="bi bi-percent"></i>
                </div>
                <div>
                  <div className="summary-label">Total Weight</div>
                  <div className="summary-value">
                    {totalWeight}%
                    {totalWeight !== 100 && (
                      <Badge color={totalWeight > 100 ? 'danger' : 'warning'} className="ms-2">
                        {totalWeight > 100 ? 'Exceeds' : 'Incomplete'}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md="4">
          <Card className="summary-card">
            <CardBody>
              <div className="summary-content">
                <div className="summary-icon bg-info">
                  <i className="bi bi-calculator"></i>
                </div>
                <div>
                  <div className="summary-label">Available Weight</div>
                  <div className="summary-value">{Math.max(0, 100 - totalWeight)}%</div>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Criteria Table */}
      <Row>
        <Col>
          <Card className="criteria-card">
            <CardBody>
              {loading ? (
                <div className="loading-spinner" style={{ minHeight: '50vh' }}>
                  <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                  <p className="mt-3">Loading criteria...</p>
                </div>
              ) : criteria.length === 0 ? (
                <div className="empty-state">
                  <i className="bi bi-inbox empty-icon"></i>
                  <h4>No Scoring Criteria Defined</h4>
                  <p>Click "Add Criteria" to create your first evaluation criterion</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="criteria-table" hover>
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>#</th>
                        <th style={{ width: '200px' }}>Label</th>
                        <th>Description</th>
                        <th style={{ width: '100px' }}>Weight</th>
                        <th style={{ width: '120px' }}>Range</th>
                        <th style={{ width: '80px' }}>Rubric</th>
                        <th style={{ width: '150px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {criteria.map((item, index) => (
                        <tr key={item.id}>
                          <td>
                            <div className="order-controls">
                              <Button
                                color="link"
                                size="sm"
                                onClick={() => handleReorder(index, 'up')}
                                disabled={index === 0}
                                className="btn-reorder"
                              >
                                <i className="bi bi-chevron-up"></i>
                              </Button>
                              <Button
                                color="link"
                                size="sm"
                                onClick={() => handleReorder(index, 'down')}
                                disabled={index === criteria.length - 1}
                                className="btn-reorder"
                              >
                                <i className="bi bi-chevron-down"></i>
                              </Button>
                            </div>
                          </td>
                          <td>
                            <strong>{item.label}</strong>
                          </td>
                          <td>
                            <div className="description-cell">{item.description}</div>
                          </td>
                          <td>
                            <Badge color="primary" className="weight-badge">
                              {item.weight}%
                            </Badge>
                          </td>
                          <td>
                            <span className="range-display">
                              {item.minRange} - {item.maxRange}
                            </span>
                          </td>
                          <td>
                            {item.rubricText && (
                              <>
                                <i 
                                  className="bi bi-file-text text-info" 
                                  id={`rubric-${item.id}`}
                                  style={{ cursor: 'pointer' }}
                                ></i>
                                <UncontrolledTooltip
                                  placement="top"
                                  target={`rubric-${item.id}`}
                                >
                                  {item.rubricText}
                                </UncontrolledTooltip>
                              </>
                            )}
                          </td>
                          <td>
                            <div className="action-buttons">
                              <Button
                                color="info"
                                size="sm"
                                onClick={() => handleEdit(item)}
                                className="btn-action"
                              >
                                <i className="bi bi-pencil"></i>
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => handleDelete(item.id)}
                                className="btn-action"
                              >
                                <i className="bi bi-trash"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg" className="criteria-modal">
        <ModalHeader toggle={toggleModal}>
          {editingCriteria ? 'Edit Scoring Criteria' : 'Add New Scoring Criteria'}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <Row>
              <Col md="8">
                <FormGroup>
                  <Label for="label">
                    Label <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    name="label"
                    id="label"
                    placeholder="e.g., Academic Excellence"
                    value={formData.label}
                    onChange={handleInputChange}
                    maxLength={100}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label for="weight">
                    Weight (0-100) <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="number"
                    name="weight"
                    id="weight"
                    placeholder="0"
                    value={formData.weight}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    required
                  />
                  <small className="text-muted">
                    Available: {100 - totalWeight + (editingCriteria ? editingCriteria.weight : 0)}%
                  </small>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label for="description">
                Description <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                name="description"
                id="description"
                placeholder="Describe what this criterion evaluates..."
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                maxLength={500}
                required
              />
              <small className="text-muted float-end">
                {formData.description.length} / 500
              </small>
            </FormGroup>

            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="minRange">
                    Minimum Score <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="number"
                    name="minRange"
                    id="minRange"
                    placeholder="0"
                    value={formData.minRange}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="maxRange">
                    Maximum Score <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="number"
                    name="maxRange"
                    id="maxRange"
                    placeholder="10"
                    value={formData.maxRange}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label for="rubricText">
                Rubric Text (Optional)
              </Label>
              <Input
                type="textarea"
                name="rubricText"
                id="rubricText"
                placeholder="Provide detailed scoring guidelines..."
                value={formData.rubricText}
                onChange={handleInputChange}
                rows="5"
                maxLength={2000}
              />
              <small className="text-muted float-end">
                {formData.rubricText.length} / 2000
              </small>
            </FormGroup>

            <Alert color="info" className="mt-3">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Tip:</strong> Use the rubric text to provide clear guidelines for evaluators on how to score this criterion.
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal} disabled={saving}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  {editingCriteria ? 'Update' : 'Add'} Criteria
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </Container>
  );
};

export default ScoringCriteria;
